<template>
    <div class="text-sm">
        <p v-if="!isEditing" class="modal-heading mb-4">New Notification</p>
        <p v-else class="modal-heading mb-4">Edit Notification</p>

        <div class="form-group">
            <label>Notification type <span class="text-danger">*</span></label>
            <div class="row">
                <div class="col-md-8">
                    <select @change="notificationTypeChanged()" v-model="form.notificationType" class="form-control">
                        <option :value="NotificationType.THRESHOLD_LIMIT">Threshold Limit</option>
                        <option :value="NotificationType.DELAYED_TEST">Delayed Test</option>
                    </select>
                </div>
            </div>
        </div>
        <div class="form-group">
            <label>Name <span class="text-danger">*</span></label>
            <div class="row">
                <div class="col-md-8">
                    <input v-model="form.name" type="text" class="form-control" placeholder="Warning" />
                </div>
            </div>
        </div>
        <div v-if="form.notificationType == NotificationType.THRESHOLD_LIMIT" class="form-group">
            <label>Threshold <span class="text-danger">*</span></label>
            <p class="mb-0">All results above set threshold will return an alert</p>
            <div class="row">
                <div class="col-md-8">
                    <div class="input-group">
                        <input v-model="form.threshold" type="text" class="form-control" placeholder="10">
                        <div class="input-group-append">
                            <span class="input-group-text">mmol/L</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="form.notificationType == NotificationType.DELAYED_TEST" class="form-group">
            <label>Time <span class="text-danger">*</span></label>
            <p class="mb-1">All results taken later than set time will return an alert</p>
            <div class="row">
                <div class="col-md-8 d-flex">
                    <div class="mr-1 flex-grow-1">
                        <label>Hour <span class="text-danger">*</span></label>
                        <select v-model="form.timeHours" :disabled="form.notificationType == NotificationType.DELAYED_TEST" class="form-control" :class="{disabled : form.notificationType == NotificationType.DELAYED_TEST}">
                            <option v-for="n in 24" :value="n | toTime" :key="n">{{ n | toTime }}</option>
                        </select>
                    </div>
                    <div class="flex-grow-1">
                        <label>Minutes <span class="text-danger">*</span></label>
                        <select v-model="form.timeMinutes" :disabled="form.notificationType == NotificationType.DELAYED_TEST" class="form-control" :class="{disabled : form.notificationType == NotificationType.DELAYED_TEST}">
                            <option value="00">00</option>
                            <option v-for="n in 11" :value="n * 5 | toTime" :key="n">{{ n * 5 | toTime }}</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-group">
            <label>Trigger <span class="text-danger">*</span></label>
            <div class="row">
                <div class="col-md-8">
                    <select v-model="form.trigger" :disabled="form.notificationType == NotificationType.DELAYED_TEST" class="form-control" :class="{disabled : form.notificationType == NotificationType.DELAYED_TEST}">
                        <option value="0">Either</option>
                        <option value="1">FGT</option>
                        <option value="2">2 Hour</option>
                    </select>
                </div>
            </div>
        </div>
        <div class="form-group">
            <label>Subject <span class="text-danger">*</span></label>
            <div class="row">
                <div class="col-md-8">
                    <p class="mb-0">Sent in email title</p>
                    <textarea v-model="form.subject" class="form-control" placeholder="A patient has posted a result that is greater than 10 mmol/L"></textarea>
                </div>
            </div>
        </div>
        <div class="form-group">
            <label>Is Active?</label>
            <b-form-checkbox v-model="form.isActive" name="check-button" switch class="custom-switch-md" >
                {{ form.isActive ? 'Yes' : 'No' }}
            </b-form-checkbox>
        </div>
        
        <p class="text-xs mb-0">The intended use of this system is as an alternative to in-clinic testing by enabling the oral glucose tolerance test (OGTT) to be performed at home as a screening tool for planned care pathways. It is not an emergency service or a substitute for urgent medical care. Healthcare professionals should not solely rely on notifications to intervene in patient care. While alerts are triggered by glucose levels exceeding set thresholds, clinical judgment and comprehensive assessment remain essential for decision-making. Please note that notifications may not always arrive promptly and are provided for informational purposes only. Use this system cautiously as a supplementary tool. By enabling this feature you acknowledge the aforementioned constraints and to use this feature only for information purposes. Any implementation deviating from the intended use should be risk assessed and captured in the healthcare organisation's own DCB0160 documentation.</p>

        <div class="d-flex align-items-center mt-4">
            <button @click="close()" :disabled="loading" class="btn btn-cancel btn-lg lg-shadow d-flex flex-grow-1 justify-content-center align-items-center" :class="{disabled : loading}">Cancel</button>
            <button @click="save()" :disabled="loading || !canSave" class="btn btn-success btn-lg lg-shadow d-flex flex-grow-1 ml-3 justify-content-center align-items-center" :class="{disabled : loading || !canSave}">{{ isEditing ? "Update" : "Add" }} <span v-if="loading" class="btn-loading"></span></button>
        </div>

        <div v-if="error" class="text-danger mt-4">Sorry, something went wrong. Please try again</div>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import NotificationType from '../../../../Common/enums/NotificationType'
import axios from 'axios'
export default {
    props: {
        model: Object
    },

    data() {
        return {
            loading: false,
            isEditing: false,
            form: {
                notificationType: 0,
                name: null,
                subject: null,
                threshold: null,
                timeHours: null,
                timeMinutes: null,
                trigger: 0, 
                isActive: true
            },
            error: false,
            NotificationType
        }
    },
    filters: {
        toTime(val) {
            if(val < 10) return '0' + val
            return val.toString()
        }
    },
    mounted() {
        if(!this.isNotificationManager) this.$emit('close')
        if(this.model != null && Object.keys(this.model).length) {
            this.isEditing = true 
            this.form.notificationType = this.model.notificationType != null ? this.model.notificationType : 0,
            this.form.name = this.model.name
            this.form.threshold = this.model.threshold,
            this.form.trigger = this.model.testTrigger,
            this.form.subject = this.model.subject
            this.form.isActive = this.model.isActive
            this.form.timeHours = this.model.timeHours,
            this.form.timeMinutes = this.model.timeMinutes
        }

        if(this.form.timeMinutes == 0) this.form.timeMinutes = "00"
    },
    computed: {
        ...mapGetters({
            domainId: 'OrganisationStore/GetDomainId',
            organisationId: 'UserStore/GetUserOrganisationId',
            isNotificationManager: 'UserStore/GetUserIsNotificationManager'
        }),

        canSave() {
            if(this.form.notificationType == this.NotificationType.THRESHOLD_LIMIT) {
                return this.form.name != null &&
                   this.form.subject != null && 
                   this.form.threshold != null &&
                   this.form.name != '' &&
                   this.form.subject != '' && 
                   this.form.threshold != '' && 
                   this.form.threshold.toString().match(/^\d+(\.\d+)*$/) &&
                   this.form.trigger != null && 
                   this.form.trigger >= 0
            }

            return this.form.name != null &&
                this.form.subject != null && 
                this.form.timeHours != null &&
                this.form.timeMinutes != null &&
                this.form.name != '' &&
                this.form.subject != '' && 
                this.form.timeHours != '' &&
                this.form.timeMinutes != '' &&
                this.form.trigger != null && 
                this.form.trigger >= 0
        },
    },
    methods: {
        ...mapActions({
            setShouldRefreshNotifications: 'AppStore/SetShouldRefreshNotifications',
            setNotification: 'NotificationStore/SetNotification'
        }),

        notificationTypeChanged() {
            if(this.form.notificationType == this.NotificationType.DELAYED_TEST) {
                this.form.timeHours = "10"
                this.form.timeMinutes = "00"
                this.form.trigger = 1
            } else {
                this.form.timeHour = null
                this.form.timeMinutes = null
                this.form.threshold = null
                this.form.trigger = 0
            }
        },

        async save() {
            const data = {
                domainId: this.domainId,
                organisationId: this.organisationId,
                notificationType: this.form.notificationType,
                name: this.form.name,
                subject: this.form.subject,
                threshold: this.form.threshold,
                testTrigger: this.form.trigger,
                isActive: this.form.isActive,
                timeHours: parseInt(this.form.timeHours),
                timeMinutes: parseInt(this.form.timeMinutes)
            }

            this.loading = true

            try {
                if(this.isEditing) {
                    await axios.patch(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/${this.model.id}`, data)
                } else {
                    await axios.post(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications`, data)
                }

                this.setShouldRefreshNotifications(true)
                this.$emit('close')
                this.setNotification({
                    type: 'success',
                    title: 'Success!',
                    message: 'The notification was saved successfully.'
                })

            } catch(err) {
                this.error = true
                this.loading = false
                this.setNotification({
                    type: 'error',
                    title: 'Error!',
                    message: 'Sorry, something went wrong. Please try again.'
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        } 
    }
}
</script>